import { createSlice } from "@reduxjs/toolkit";
import {
  fetchChatHistory,
  fetchChatUsers,
  initChat,
  fetchMyChats,
  fetchMoreChatHistory,
  fetchChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  addChannelMembers,
  removeChannelMembers,
  fetchUnreadMessages,
  fetchUnreadCounts,
  markChatAsRead,
} from "./chatThunks.js";

const initialState = {
  chatUsers: [],
  myChats: [],
  channels: [],
  messagesByChatId: {},
  historyMeta: {},
  chatId: null,
  loading: false,
  error: null,
  unreadByChatId: {},
  totalUnread: 0,
  typingByChatId: {},
};

const chatsSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        upsertChat(state, action) {
            const incoming = action.payload;
            if (!incoming?.id) return;
            const index = state.myChats.findIndex((c) => c.id === incoming.id);
            if (index >= 0) {
                const prev = state.myChats[index];
                const nextUpdate = incoming.updated_at || prev.updated_at;
                state.myChats[index] = { ...prev, ...incoming, updated_at: nextUpdate };
            } else {
                state.myChats.unshift(incoming);
            }
        },
        addMessage(state, action) {
            const message = action.payload;
            const chatId = message.chat_id;
            if (!chatId && chatId !== 0) return;

            const key = String(chatId);

            if (!state.messagesByChatId[key]) {
                state.messagesByChatId[key] = [];
            }

            // ✅ DEDUPE: evita meter el mismo mensaje 2 veces (WS + respuesta, o WS duplicado)
            if (message?.id && state.messagesByChatId[key].some((m) => m.id === message.id)) {
                return;
            }

            state.messagesByChatId[key].push(message);

            const updatedAt = message.timestamp || new Date().toISOString();
            const index = state.myChats.findIndex((c) => String(c.id) === key);
            if (index >= 0) {
                state.myChats[index] = {
                    ...state.myChats[index],
                    updated_at: updatedAt,
                };
            } else {
                const sender =
                    message.sender || (message.sender_id ? { id: message.sender_id } : null);
                const receiver =
                    message.receiver || (message.receiver_id ? { id: message.receiver_id } : null);
                const participants = [sender, receiver].filter(Boolean);
                state.myChats.unshift({
                    id: chatId,
                    participants,
                    sender: sender || null,
                    receiver: receiver || null,
                    updated_at: updatedAt,
                });
            }
        },

        clearMessages(state) {
            const currentChatId = state.chatId;
            if (currentChatId != null) {
                const key = String(currentChatId);

                if (state.messagesByChatId && state.messagesByChatId[key]) {
                    delete state.messagesByChatId[key];
                }
                if (state.historyMeta && state.historyMeta[key]) {
                    delete state.historyMeta[key];
                }
                if (state.typingByChatId && state.typingByChatId[key]) {
                    delete state.typingByChatId[key];
                }
                if (state.unreadByChatId && state.unreadByChatId[key] != null) {
                    delete state.unreadByChatId[key];
                }
            }
            state.chatId = null;
        },
        // 🔹 NUEVO: manejar usuarios escribiendo por chat
        setUserTyping(state, action) {
            const { chatId, userId, isTyping } = action.payload || {};
            if (chatId == null || userId == null) return;

            const key = String(chatId);

            if (!state.typingByChatId) {
                state.typingByChatId = {};
            }
            if (!state.typingByChatId[key]) {
                state.typingByChatId[key] = [];
            }

            const list = state.typingByChatId[key];
            const index = list.indexOf(userId);

            if (isTyping) {
                if (index === -1) list.push(userId);
            } else {
                if (index !== -1) {
                    list.splice(index, 1);
                }
                if (list.length === 0) {
                    delete state.typingByChatId[key];
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Chat History
            .addCase(fetchChatHistory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChatHistory.fulfilled, (state, action) => {
                state.loading = false;
                const { chatId, messages, nextCursor, isLoadMore } = action.payload;
                const key = String(chatId);
                if (!state.messagesByChatId[key]) {
                    state.messagesByChatId[key] = [];
                }
                if (isLoadMore) {
                    state.messagesByChatId[key] = [
                        ...messages,
                        ...state.messagesByChatId[key],
                    ];
                } else {
                    state.messagesByChatId[key] = messages;
                    state.chatId = chatId;
                }
                state.historyMeta[key] = { nextCursor };
            })
            .addCase(fetchChatHistory.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload || "Error al cargar el historial del chat";
            })

            // Fetch Chat Users
            .addCase(fetchChatUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChatUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.chatUsers = Array.isArray(action.payload)
                    ? action.payload
                    : [];
            })
            .addCase(fetchChatUsers.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload || "Error al obtener los usuarios de chat";
            })

            // Init Chat
            .addCase(initChat.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(initChat.fulfilled, (state, action) => {
                state.loading = false;
                state.chatId = action.payload.chat_id;
            })
            .addCase(initChat.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Error al iniciar chat.";
            })

            // Get Old Chats (DM + grupos)
            .addCase(fetchMyChats.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMyChats.fulfilled, (state, action) => {
                state.loading = false;
                state.myChats = action.payload;
            })
            .addCase(fetchMyChats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Error al obtener tus chats";
            })

            // Get More History
            .addCase(fetchMoreChatHistory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMoreChatHistory.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(fetchMoreChatHistory.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload || "Error al cargar más mensajes anteriores.";
            })

            // Fetch Channels
            .addCase(fetchChannels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChannels.fulfilled, (state, action) => {
                state.loading = false;
                state.channels = Array.isArray(action.payload)
                    ? action.payload
                    : [];
            })
            .addCase(fetchChannels.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Error al obtener los canales";
            })

            // Create Channel
            .addCase(createChannel.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createChannel.fulfilled, (state, action) => {
                state.loading = false;
                const channel = action.payload;
                if (!channel || !channel.id) return;

                // Actualizar lista de canales
                const channelIndex = state.channels.findIndex(
                    (c) => c.id === channel.id
                );
                if (channelIndex >= 0) {
                    state.channels[channelIndex] = {
                        ...state.channels[channelIndex],
                        ...channel,
                    };
                } else {
                    state.channels.unshift(channel);
                }

                // También mantenerlo en myChats
                const chatIndex = state.myChats.findIndex(
                    (c) => c.id === channel.id
                );
                if (chatIndex >= 0) {
                    state.myChats[chatIndex] = {
                        ...state.myChats[chatIndex],
                        ...channel,
                    };
                } else {
                    state.myChats.unshift(channel);
                }
            })
            .addCase(createChannel.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Error al crear el canal";
            })

            // Agregar miembros a un canal
            .addCase(addChannelMembers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addChannelMembers.fulfilled, (state, action) => {
                state.loading = false;
                const channel = action.payload;
                if (!channel || !channel.id) return;

                // Actualizar lista de canales
                const channelIndex = state.channels.findIndex(
                    (c) => c.id === channel.id
                );
                if (channelIndex >= 0) {
                    state.channels[channelIndex] = {
                        ...state.channels[channelIndex],
                        ...channel,
                    };
                } else {
                    state.channels.unshift(channel);
                }

                // También en myChats
                const chatIndex = state.myChats.findIndex(
                    (c) => c.id === channel.id
                );
                if (chatIndex >= 0) {
                    state.myChats[chatIndex] = {
                        ...state.myChats[chatIndex],
                        ...channel,
                    };
                } else {
                    state.myChats.unshift(channel);
                }
            })
            .addCase(addChannelMembers.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload || "Error al agregar usuarios al canal";
            })

            .addCase(removeChannelMembers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeChannelMembers.fulfilled, (state, action) => {
                state.loading = false;
                const channel = action.payload;
                if (!channel || !channel.id) return;

                const channelIndex = state.channels.findIndex((c) => c.id === channel.id);
                if (channelIndex >= 0) {
                    state.channels[channelIndex] = { ...state.channels[channelIndex], ...channel };
                } else {
                    state.channels.unshift(channel);
                }

                const chatIndex = state.myChats.findIndex((c) => c.id === channel.id);
                if (chatIndex >= 0) {
                    state.myChats[chatIndex] = { ...state.myChats[chatIndex], ...channel };
                } else {
                    state.myChats.unshift(channel);
                }
            })
            .addCase(removeChannelMembers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Error al quitar usuarios del canal";
            })


            // Update Channel
            .addCase(updateChannel.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateChannel.fulfilled, (state, action) => {
                state.loading = false;
                const channel = action.payload;
                if (!channel || !channel.id) return;

                const channelIndex = state.channels.findIndex((c) => c.id === channel.id);
                if (channelIndex >= 0) {
                    state.channels[channelIndex] = { ...state.channels[channelIndex], ...channel };
                } else {
                    state.channels.unshift(channel);
                }

                const chatIndex = state.myChats.findIndex((c) => c.id === channel.id);
                if (chatIndex >= 0) {
                    state.myChats[chatIndex] = { ...state.myChats[chatIndex], ...channel };
                }
            })
            .addCase(updateChannel.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Error al actualizar el canal";
            })

            // Delete Channel
            .addCase(deleteChannel.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteChannel.fulfilled, (state, action) => {
                state.loading = false;

                const channelId = action.payload?.channelId;
                if (channelId == null) return;

                const key = String(channelId);

                state.channels = state.channels.filter((c) => String(c.id) !== key);
                state.myChats = state.myChats.filter((c) => String(c.id) !== key);

                if (state.messagesByChatId?.[key]) delete state.messagesByChatId[key];
                if (state.historyMeta?.[key]) delete state.historyMeta[key];
                if (state.typingByChatId?.[key]) delete state.typingByChatId[key];

                const currentUnread = state.unreadByChatId?.[key] || 0;
                state.totalUnread = Math.max(0, state.totalUnread - currentUnread);
                if (state.unreadByChatId?.[key] != null) delete state.unreadByChatId[key];

                if (String(state.chatId) === key) {
                    state.chatId = null;
                }
            })
            .addCase(deleteChannel.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Error al eliminar el canal";
            })


            // 🔹 Fetch unread counts (notificaciones)
            .addCase(fetchUnreadCounts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUnreadCounts.fulfilled, (state, action) => {
                state.loading = false;

                const unreadByChatId = {};
                let total = 0;

                const items = Array.isArray(action.payload) ? action.payload : [];
                items.forEach((item) => {
                    const id = item.chat_id ?? item.chat ?? item.id;
                    if (id == null) return;

                    const key = String(id);
                    const count = item.count ?? 0;

                    unreadByChatId[key] = count;
                    total += count;
                });

                state.unreadByChatId = unreadByChatId;
                state.totalUnread = total;
            })
            .addCase(fetchUnreadCounts.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload ||
                    "Error al obtener las notificaciones de chat";
            })


            // 🔹 Obtener mensajes no leídos
            .addCase(fetchUnreadMessages.pending, (state) => {
                state.error = null;
            })
            .addCase(fetchUnreadMessages.fulfilled, (state, action) => {
                const list = Array.isArray(action.payload) ? action.payload : [];
                const unreadMap = {};
                let total = 0;

                list.forEach((item) => {
                    const chatId = item.chat_id;
                    const count = item.count || 0;
                    if (chatId != null) {
                        unreadMap[chatId] = count;
                        total += count;
                    }
                });

                state.unreadByChatId = unreadMap;
                state.totalUnread = total;
            })
            .addCase(fetchUnreadMessages.rejected, (state, action) => {
                state.error = action.payload || "Error al obtener los mensajes no leídos";
            })

            // 🔹 Marcar chat como leído
            .addCase(markChatAsRead.pending, (state) => {
                state.error = null;
            })
            .addCase(markChatAsRead.fulfilled, (state, action) => {
                const chatId = action.payload?.chatId;
                if (chatId == null) return;

                const key = String(chatId);
                const current = state.unreadByChatId[key] || 0;

                state.totalUnread = Math.max(0, state.totalUnread - current);
                state.unreadByChatId[key] = 0;
            })
            .addCase(markChatAsRead.rejected, (state, action) => {
                state.error =
                    action.payload || "Error al marcar el chat como leído";
            });
    },
});

export const { addMessage, clearMessages, upsertChat, setUserTyping } =
    chatsSlice.actions;
export default chatsSlice.reducer;